// ============================================================================
// CHANGO V5.0 — Ops CMS · Site-wide notices (Task #51 Phase 1)
// ============================================================================
// Route: /ops/cms/notices       — admin only
//
// A 'notice' = site-wide banner / alert driven by the site_notices table.
// Ops can schedule a banner (starts_at / ends_at), flip severity, and publish
// via status: draft | scheduled | live. Status expired/archived is read-only
// from the editor (the public API treats them as invisible already).
//
// Backed by:
//   GET    /api/ops/site/notices
//   POST   /api/ops/site/notices                      (upsert by key_code+locale)
//   DELETE /api/ops/site/notices/:keyCode?locale=…    (soft-archive)
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, HttpError } from '../../../api/client.js';
import { useAuth } from '../../../hooks/useAuth.js';
import {
  normalizeNotice,
  isoToLocalInput,
  localInputToIso,
  formatDateTime,
  NOTICE_SEVERITIES,
  NOTICE_STATUS_EDITABLE,
  LIMITS,
} from './cmsHelpers.js';

function Banner({ kind, children }) {
  if (!children) return null;
  const tone = {
    error: 'border-rose-500/40 bg-rose-900/30 text-rose-200',
    ok:    'border-emerald-500/40 bg-emerald-900/30 text-emerald-200',
    warn:  'border-amber-500/40 bg-amber-900/30 text-amber-200',
    info:  'border-slate-500/40 bg-slate-800/60 text-slate-200',
  }[kind || 'info'];
  return (
    <div className={`mb-4 rounded-md border px-4 py-2 text-sm ${tone}`}>{children}</div>
  );
}

function SeverityBadge({ severity }) {
  const cfg = {
    info:     ['bg-slate-700 text-slate-200',       '通知'],
    success:  ['bg-emerald-700 text-emerald-100',   '成功'],
    warning:  ['bg-amber-700 text-amber-100',       '警告'],
    critical: ['bg-rose-700 text-rose-100',         '严重'],
  }[severity] || ['bg-slate-700 text-slate-200', severity || '—'];
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${cfg[0]}`}>{cfg[1]}</span>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    draft:     ['bg-slate-700 text-slate-200',       '草稿'],
    scheduled: ['bg-sky-700 text-sky-100',           '已排期'],
    live:      ['bg-emerald-700 text-emerald-100',   '已上线'],
    expired:   ['bg-slate-600 text-slate-200',       '已过期'],
    archived:  ['bg-slate-800 text-slate-400',       '已归档'],
  }[status] || ['bg-slate-700 text-slate-200', status || '—'];
  return (
    <span className={`rounded px-2 py-0.5 text-xs ${cfg[0]}`}>{cfg[1]}</span>
  );
}

const EMPTY_FORM = {
  key_code: '',
  title: '',
  severity: 'info',
  status: 'draft',
  body_md: '',
  action_label: '',
  action_href: '',
  dismissible: true,
  locale: 'zh-CN',
  starts_at: '', // local datetime-local string
  ends_at: '',
};

function useNotices() {
  const [state, setState] = useState({ items: [], loading: true, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((x) => x + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const res = await api.get('/api/ops/site/notices');
        if (cancelled) return;
        setState({
          items: Array.isArray(res?.items) ? res.items : [],
          loading: false, error: null,
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HttpError && err.status === 404) {
          setState({ items: [], loading: false, error: null });
        } else {
          setState((s) => ({ ...s, loading: false, error: err.message || '加载失败' }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  return { ...state, reload };
}

export default function OpsCmsNotices() {
  const { user } = useAuth();
  const { items, loading, error, reload } = useNotices();
  const [form, setForm] = useState(EMPTY_FORM);
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const sorted = useMemo(() => {
    const rank = { live: 0, scheduled: 1, draft: 2, expired: 3, archived: 4 };
    return [...items].sort((a, b) => {
      const ra = rank[a.status] ?? 99;
      const rb = rank[b.status] ?? 99;
      if (ra !== rb) return ra - rb;
      return String(a.key_code).localeCompare(String(b.key_code));
    });
  }, [items]);

  const validation = useMemo(() => {
    return normalizeNotice({
      ...form,
      starts_at: localInputToIso(form.starts_at),
      ends_at:   localInputToIso(form.ends_at),
    });
  }, [form]);

  const onSubmit = async () => {
    if (!validation.ok) {
      setBanner({ kind: 'error', msg: `${validation.field}: ${validation.message}` });
      return;
    }
    setBusy(true);
    setBanner(null);
    try {
      await api.post('/api/ops/site/notices', validation.value);
      setBanner({ kind: 'ok', msg: `已保存公告 ${validation.value.key_code}` });
      setForm(EMPTY_FORM);
      reload();
    } catch (err) {
      setBanner({ kind: 'error', msg: err.message || '保存失败' });
    } finally {
      setBusy(false);
    }
  };

  const onEdit = (n) => {
    setForm({
      key_code: n.key_code,
      title: n.title,
      severity: NOTICE_SEVERITIES.includes(n.severity) ? n.severity : 'info',
      status: NOTICE_STATUS_EDITABLE.includes(n.status) ? n.status : 'draft',
      body_md: n.body_md ?? '',
      action_label: n.action_label ?? '',
      action_href: n.action_href ?? '',
      dismissible: n.dismissible !== 0,
      locale: n.locale || 'zh-CN',
      starts_at: isoToLocalInput(n.starts_at),
      ends_at:   isoToLocalInput(n.ends_at),
    });
    setBanner(null);
    window?.scrollTo?.({ top: 0, behavior: 'smooth' });
  };

  const onArchive = async (n) => {
    if (!confirm(`归档公告 ${n.key_code} (${n.locale})？归档后前端不再展示，但历史记录保留。`)) return;
    try {
      await api.delete(`/api/ops/site/notices/${encodeURIComponent(n.key_code)}?locale=${encodeURIComponent(n.locale)}`);
      setBanner({ kind: 'ok', msg: `已归档 ${n.key_code}` });
      reload();
    } catch (err) {
      setBanner({ kind: 'error', msg: err.message || '归档失败' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-700 bg-slate-800/80">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">CMS · 全站公告</h1>
            <p className="text-xs text-slate-400">
              {user?.email} · {user?.role} ·
              {' '}<Link to="/ops/cms/pages" className="underline">页面管理</Link>
              {' · '}<Link to="/ops/dashboard" className="underline">返回 Ops</Link>
            </p>
          </div>
          <button type="button" onClick={reload}
            className="rounded border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700">
            刷新
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        {banner && <Banner kind={banner.kind}>{banner.msg}</Banner>}
        {error && <Banner kind="error">{error}</Banner>}

        {/* --- Editor card ------------------------------------------------ */}
        <section className="mb-8 rounded-lg border border-slate-700 bg-slate-800/40 p-5">
          <h2 className="text-sm font-semibold text-slate-200">新增 / 编辑公告</h2>
          <p className="mt-1 text-xs text-slate-400">
            按 key_code + locale 去重；已存在的 key 会被覆盖（upsert）。状态 live 会立即展示，scheduled 会在 starts_at 到达时展示。
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-slate-200">Key Code <span className="text-rose-400">*</span></span>
              <input type="text"
                value={form.key_code}
                onChange={(e) => setField('key_code', e.target.value)}
                maxLength={80}
                placeholder="例如 launch_2026_banner"
                className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm font-mono text-slate-100 focus:border-chango-500 focus:outline-none" />
              <span className="mt-1 block text-xs text-slate-400">仅字母 / 数字 / _-.</span>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-200">标题 <span className="text-rose-400">*</span></span>
              <input type="text"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                maxLength={LIMITS.SHORT_TEXT}
                className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none" />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-200">严重级别</span>
              <select
                value={form.severity}
                onChange={(e) => setField('severity', e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none">
                {NOTICE_SEVERITIES.map((x) => (
                  <option key={x} value={x}>{x}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-200">状态</span>
              <select
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none">
                {NOTICE_STATUS_EDITABLE.map((x) => (
                  <option key={x} value={x}>{x}</option>
                ))}
              </select>
            </label>

            <label className="block md:col-span-2">
              <span className="text-sm font-medium text-slate-200">正文 (Markdown)</span>
              <textarea rows={4}
                value={form.body_md}
                onChange={(e) => setField('body_md', e.target.value)}
                maxLength={LIMITS.NOTICE_BODY}
                className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none" />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-200">动作文案</span>
              <input type="text"
                value={form.action_label}
                onChange={(e) => setField('action_label', e.target.value)}
                maxLength={LIMITS.CTA_LABEL}
                placeholder="例如 查看详情"
                className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-200">动作链接</span>
              <input type="text"
                value={form.action_href}
                onChange={(e) => setField('action_href', e.target.value)}
                maxLength={LIMITS.URL}
                placeholder="/pricing 或 https://…"
                className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none" />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-200">开始时间</span>
              <input type="datetime-local"
                value={form.starts_at}
                onChange={(e) => setField('starts_at', e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none" />
              <span className="mt-1 block text-xs text-slate-400">留空 = 立即生效</span>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-200">结束时间</span>
              <input type="datetime-local"
                value={form.ends_at}
                onChange={(e) => setField('ends_at', e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none" />
              <span className="mt-1 block text-xs text-slate-400">留空 = 永不过期</span>
            </label>

            <label className="flex items-center gap-2 text-sm text-slate-200 md:col-span-2">
              <input type="checkbox"
                checked={form.dismissible}
                onChange={(e) => setField('dismissible', e.target.checked)} />
              允许访客手动关闭
            </label>
          </div>

          {!validation.ok && (
            <p className="mt-3 text-xs text-amber-300">{validation.field}: {validation.message}</p>
          )}

          <div className="mt-4 flex items-center gap-2">
            <button type="button"
              onClick={onSubmit}
              disabled={busy || !validation.ok}
              className="rounded bg-chango-600 px-4 py-2 text-sm font-semibold text-white hover:bg-chango-700 disabled:opacity-50">
              {busy ? '保存中…' : '保存公告'}
            </button>
            <button type="button"
              onClick={() => setForm(EMPTY_FORM)}
              className="rounded border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:bg-slate-700">
              重置表单
            </button>
          </div>
        </section>

        {/* --- Existing notices ------------------------------------------ */}
        <section className="overflow-hidden rounded-lg border border-slate-700 bg-slate-800/40">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/80 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3">Key</th>
                <th className="px-4 py-3">标题</th>
                <th className="px-4 py-3">级别</th>
                <th className="px-4 py-3">状态</th>
                <th className="px-4 py-3">时间窗口</th>
                <th className="px-4 py-3">locale</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">加载中…</td></tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">尚无公告</td></tr>
              )}
              {!loading && sorted.map((n) => (
                <tr key={`${n.key_code}::${n.locale}`} className="border-t border-slate-700/60 hover:bg-slate-700/20">
                  <td className="px-4 py-3 font-mono text-xs">{n.key_code}</td>
                  <td className="px-4 py-3">{n.title}</td>
                  <td className="px-4 py-3"><SeverityBadge severity={n.severity} /></td>
                  <td className="px-4 py-3"><StatusBadge status={n.status} /></td>
                  <td className="px-4 py-3 text-xs text-slate-300">
                    {formatDateTime(n.starts_at)} → {formatDateTime(n.ends_at)}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{n.locale}</td>
                  <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                    <button type="button" onClick={() => onEdit(n)}
                      className="rounded border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700">
                      编辑
                    </button>
                    {n.status !== 'archived' && (
                      <button type="button" onClick={() => onArchive(n)}
                        className="rounded bg-amber-700/60 px-3 py-1 text-xs text-white hover:bg-amber-700">
                        归档
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
}
